/**
 * #5068 — parts placed by their relation to other parts, and the layout check that runs after every beat.
 *
 * A model guessing coordinates puts a piston below its cylinder and an arrow's tip in empty paper. A program can
 * instead say where a part goes relative to parts already drawn, and the kit works out the exact spot:
 * - `inside: id` — centred in that part (a piston in its cylinder, a flame in a burner);
 * - `at: id | [id, anchor]` — on that part's centre or named anchor (a crank's "pin", a lever's "pivot");
 * - `beside: { of, side, gap }` — next to that part, clear of it;
 * - `between: [a, b]` — from a to b, ending on each part's outline (or exactly on a named anchor): the end points of an
 *   arrow, rod, spring, pipe or wire, or a shaped part (a shaft, a bar) laid along that line.
 *
 * After each beat, the layout check pulls the beat's parts onto the sheet and moves a part off another it partly
 * overlaps, unless one sits inside the other, one is far smaller (mounted on it), they are placed relative to each
 * other, or a joint links them.
 */
import type { DrawingPart } from './DrawingSchema';
import { DRAWING_HEIGHT, DRAWING_WIDTH } from './DrawingSchema';
import { isAbsoluteDrawingKit, placeDrawingPart } from './drawingParts';
import { PARTS_KIT_3D } from './partsKit3d';
import { SKETCH_KIT, type Point } from './sketchKit';

export type DrawingPlacementRef = string | [string, string];
export interface DrawingPlacement {
  inside?: string;
  at?: DrawingPlacementRef;
  beside?: { of: string; side: 'left' | 'right' | 'above' | 'below'; gap?: number };
  between?: [DrawingPlacementRef, DrawingPlacementRef];
}
type Box = { x: number; y: number; width: number; height: number };
export class DrawingPlacementError extends Error {}

const center = (box: Box): Point => [box.x + box.width / 2, box.y + box.height / 2];
/** Where a ray from the box's centre towards `toward` leaves the box, `gap` beyond it. */
function edgeToward(box: Box, toward: Point, gap = 6): Point {
  const [cx, cy] = center(box), dx = toward[0] - cx, dy = toward[1] - cy, length = Math.hypot(dx, dy);
  if (length < 1e-6) return [cx, cy];
  const t = Math.min(dx ? box.width / 2 / Math.abs(dx) : Infinity, dy ? box.height / 2 / Math.abs(dy) : Infinity);
  return [cx + dx * t + dx / length * gap, cy + dy * t + dy / length * gap];
}
/** The ends a sheet-points kit takes: `from`/`to`, or a `points` list. */
function endsParam(kit: string): 'fromTo' | 'points' | null {
  const item = (SKETCH_KIT as Record<string, { params: { shape?: Record<string, unknown> } }>)[kit]
    ?? (PARTS_KIT_3D as Record<string, { params: { shape?: Record<string, unknown> } }>)[kit];
  const shape = item?.params.shape ?? {};
  return 'from' in shape ? 'fromTo' : 'points' in shape ? 'points' : null;
}

/**
 * Turns a placement into coordinates (and end points) for a part about to be drawn. `part` is the part as it would be
 * placed without the relation, so its own footprint is known. Returns the fields to override and the parts it is
 * placed relative to.
 */
export function resolveDrawingPlacement(placement: DrawingPlacement, part: DrawingPart, parts: readonly DrawingPart[]):
  { x?: number; y?: number; size?: number; rotate?: number; params?: Record<string, unknown>; parents: string[] } {
  const find = (id: unknown) => {
    const target = typeof id === 'string' ? parts.find(item => item.id === id) : undefined;
    if (!target) throw new DrawingPlacementError(`Part "${part.id}": no part "${String(id).slice(0, 40)}" to place it by (draw that part first)`);
    const placed = placeDrawingPart(target);
    if (!placed) throw new DrawingPlacementError(`Part "${part.id}": "${target.id}" has no shape to place by`);
    return { target, placed };
  };
  const point = (ref: DrawingPlacementRef, toward?: Point): { point: Point; id: string } => {
    const [id, anchor] = Array.isArray(ref) ? ref : [ref, null];
    const { target, placed } = find(id);
    if (anchor !== null && anchor !== undefined) {
      // Every part has a centre, whether or not its kit names one.
      const found = placed.anchors[String(anchor)] ?? (String(anchor) === 'center' ? center(placed.box) : undefined);
      if (!found) throw new DrawingPlacementError(`Part "${part.id}": "${target.id}" has no anchor "${String(anchor).slice(0, 20)}"${Object.keys(placed.anchors).length ? ` (it has ${Object.keys(placed.anchors).join(', ')})` : ''}`);
      return { point: found, id: target.id };
    }
    return { point: toward ? edgeToward(placed.box, toward) : center(placed.box), id: target.id };
  };
  if (placement.between) {
    const [a, b] = placement.between;
    // Each end aims at the other end's part, so plain ids give an arrow from outline to outline.
    const aim = (ref: DrawingPlacementRef) => { const { placed } = find(Array.isArray(ref) ? ref[0] : ref); return Array.isArray(ref) ? point(ref).point : center(placed.box); };
    const from = point(a, aim(b)), to = point(b, aim(a));
    // A shaped part (a shaft, a bar) is laid along the line: centred on it, as long as it, turned to it.
    if (!isAbsoluteDrawingKit(part.kit)) {
      const dx = to.point[0] - from.point[0], dy = to.point[1] - from.point[1];
      return { x: (from.point[0] + to.point[0]) / 2, y: (from.point[1] + to.point[1]) / 2, size: Math.max(4, Math.hypot(dx, dy)),
        rotate: Math.atan2(dy, dx) * 180 / Math.PI, parents: [from.id, to.id] };
    }
    const ends = endsParam(part.kit);
    const params = ends === 'points' ? { ...part.params, points: [from.point, to.point] } : { ...part.params, from: from.point, to: to.point };
    return { params, parents: [from.id, to.id] };
  }
  if (isAbsoluteDrawingKit(part.kit)) throw new DrawingPlacementError(`Part "${part.id}": a part drawn from sheet points is placed with between, or by its points`);
  if (placement.inside) {
    const { target, placed } = find(placement.inside);
    const [x, y] = center(placed.box);
    // Unless a size was chosen, it fits well inside.
    const size = Math.min(part.size, Math.max(4, placed.box.width * .7));
    return { x, y, size, parents: [target.id] };
  }
  if (placement.at) {
    const { point: [x, y], id } = point(placement.at);
    return { x, y, parents: [id] };
  }
  if (placement.beside) {
    const { target, placed } = find(placement.beside.of);
    const own = placeDrawingPart(part);
    if (!own) return { parents: [target.id] };
    const gap = Math.max(0, Math.min(300, Number(placement.beside.gap ?? 40) || 0)), t = placed.box, o = own.box;
    // Offsets from the part's origin to its footprint's edges.
    const left = part.x - o.x, right = o.x + o.width - part.x, top = part.y - o.y, bottom = o.y + o.height - part.y;
    const [cx, cy] = center(t), ox = center(o)[0] - part.x, oy = center(o)[1] - part.y;
    const side = placement.beside.side;
    if (side === 'right') return { x: t.x + t.width + gap + left, y: cy - oy, parents: [target.id] };
    if (side === 'left') return { x: t.x - gap - right, y: cy - oy, parents: [target.id] };
    if (side === 'above') return { x: cx - ox, y: t.y - gap - bottom, parents: [target.id] };
    return { x: cx - ox, y: t.y + t.height + gap + top, parents: [target.id] };
  }
  return { parents: [] };
}

const area = (box: Box) => Math.max(0, box.width) * Math.max(0, box.height);
const intersection = (a: Box, b: Box) =>
  Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x)) * Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
/** Nested: mostly inside a clearly larger part (a piston in its cylinder), not two similar parts that happen to overlap. */
const contains = (outer: Box, inner: Box) => area(inner) <= .8 * area(outer) && intersection(outer, inner) >= .85 * area(inner);

/**
 * The layout check after a beat: parts from `from` on (this beat's) are kept on the sheet and moved off parts they
 * partly overlap. Parts that move carry the parts placed relative to them; parts in a joint never move (their motion
 * is derived from where they are). Returns the parts with new positions, and one note per move.
 */
export function settleDrawingBeat(parts: DrawingPart[], from: number, related: (a: string, b: string) => boolean, jointed: ReadonlySet<string>,
  childrenOf: (id: string) => string[]): { parts: DrawingPart[]; notes: string[] } {
  const out = [...parts], notes: string[] = [], margin = 6;
  const boxOf = (part: DrawingPart) => placeDrawingPart(part)?.box ?? null;
  const shift = (index: number, dx: number, dy: number, seen = new Set<string>()) => {
    const part = out[index];
    if (seen.has(part.id)) return;
    seen.add(part.id);
    out[index] = { ...part, x: part.x + dx, y: part.y + dy };
    for (const child of childrenOf(part.id)) {
      const childIndex = out.findIndex(item => item.id === child);
      if (childIndex >= from && !isAbsoluteDrawingKit(out[childIndex].kit)) shift(childIndex, dx, dy, seen);
    }
  };
  for (let index = from; index < out.length; index++) {
    const part = out[index];
    if (isAbsoluteDrawingKit(part.kit) || jointed.has(part.id)) continue;
    let box = boxOf(part);
    if (!box) continue;
    // Pull it onto the sheet (when it fits at all).
    const dx = box.width > DRAWING_WIDTH - 2 * margin ? 0 : box.x < margin ? margin - box.x : box.x + box.width > DRAWING_WIDTH - margin ? DRAWING_WIDTH - margin - box.x - box.width : 0;
    const dy = box.height > DRAWING_HEIGHT - 2 * margin ? 0 : box.y < margin ? margin - box.y : box.y + box.height > DRAWING_HEIGHT - margin ? DRAWING_HEIGHT - margin - box.y - box.height : 0;
    if (dx || dy) { shift(index, dx, dy); notes.push(`Moved "${part.id}" onto the sheet`); box = boxOf(out[index])!; }
    // Then off any part it partly overlaps without belonging to it.
    for (let pass = 0; pass < 4; pass++) {
      const clash = out.slice(0, index).map(other => ({ other, otherBox: !isAbsoluteDrawingKit(other.kit) ? boxOf(other) : null }))
        .find(({ other, otherBox }) => otherBox && !related(part.id, other.id) && !contains(otherBox, box!) && !contains(box!, otherBox)
          // A much smaller part touching a bigger one is mounted on it (a spark plug on a cylinder head), not a clash.
          && Math.min(area(box!), area(otherBox)) >= .2 * Math.max(area(box!), area(otherBox))
          && intersection(box!, otherBox) > .12 * Math.min(area(box!), area(otherBox)));
      if (!clash) break;
      const o = clash.otherBox!, gap = 10;
      // The shortest move that clears it and stays on the sheet.
      const moves: Point[] = [[o.x + o.width + gap - box.x, 0], [o.x - gap - box.x - box.width, 0], [0, o.y + o.height + gap - box.y], [0, o.y - gap - box.y - box.height]];
      const fits = ([mx, my]: Point) => box!.x + mx >= margin && box!.x + box!.width + mx <= DRAWING_WIDTH - margin && box!.y + my >= margin && box!.y + box!.height + my <= DRAWING_HEIGHT - margin;
      const move = moves.filter(fits).sort((a, b) => Math.hypot(...a) - Math.hypot(...b))[0];
      if (!move) break;
      shift(index, move[0], move[1]);
      notes.push(`Moved "${part.id}" off "${clash.other.id}"`);
      box = boxOf(out[index])!;
    }
  }
  return { parts: out, notes };
}
